import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  EditorAudioController,
  type LoopRegionBeats,
} from '../game/audio/editorAudioController';
import { preloadAudioFile } from '../game/audio/bufferCache';

export type { LoopRegionBeats } from '../game/audio/editorAudioController';

export interface UseEditorAudioPreviewArgs {
  /** Path under public/, e.g. "/audio/guitar/heroes.mp3". */
  src: string;
  /** Audio tempo — converts beats ↔ seconds. Matches chart.bpm. */
  bpm: number;
  /** Milliseconds of intro to skip before beat 0. */
  offsetMs: number;
  /** Chart length; forward playback settles here rather than at file end. */
  totalBeats: number;
}

export interface EditorAudioPreview {
  /** True for any playback (forward or loop). */
  isPlaying: boolean;
  /** True only while repeating a loop window. */
  isLooping: boolean;
  /** The selected loop window; persists while stopped so re-loop reuses it. */
  loopRegion: LoopRegionBeats | null;
  /**
   * Live playback head in beats while playing; the settled head otherwise.
   * Updated every animation frame during playback so callers can draw a
   * sweeping playhead / follow-scroll the view.
   */
  playheadBeat: number;
  /** Last preview error (decode / autoplay), surfaced to the editor UI. */
  error: string | null;
  /** Play forward from `beat` to the end of the chart (clears loop mode). */
  playFrom: (beat: number) => void;
  /**
   * Toggle forward playback from `beat` (Space). Pauses while already playing
   * forward; switches a running loop into forward play; otherwise starts.
   */
  toggleForward: (beat: number) => void;
  /** Stop playback, keeping the loop selection for a later re-loop. */
  pause: () => void;
  /**
   * Start (or restart) looping `region`. Falls back to the current selection
   * when omitted; a no-op when neither exists.
   */
  startLoop: (region?: LoopRegionBeats) => void;
  /** Toggle the loop: pause if looping, else {@link startLoop}. */
  toggleLoop: (fallback?: LoopRegionBeats) => void;
  /** Drop the loop selection, stopping playback if a loop is running. */
  clearLoop: () => void;
}

/**
 * Shared preview-audio engine for the Guitar + Pulse map editors. Owns the
 * {@link EditorAudioController} lifecycle, a requestAnimationFrame playhead
 * pump, and the play / loop state machine, so both editors get identical,
 * gapless "hear the bit you're editing" behaviour while each renders its own
 * playhead + loop band over its own timeline.
 *
 * The controller is recreated when the backing track (`src` / `bpm` /
 * `offsetMs`) changes; the loop selection resets with it.
 */
export function useEditorAudioPreview({
  src,
  bpm,
  offsetMs,
  totalBeats,
}: UseEditorAudioPreviewArgs): EditorAudioPreview {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLooping, setIsLooping] = useState(false);
  const [loopRegion, setLoopRegionState] = useState<LoopRegionBeats | null>(null);
  const [playheadBeat, setPlayheadBeat] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const controllerRef = useRef<EditorAudioController | null>(null);
  const loopRegionRef = useRef<LoopRegionBeats | null>(null);
  const totalBeatsRef = useRef(totalBeats);

  useEffect(() => {
    loopRegionRef.current = loopRegion;
  }, [loopRegion]);
  useEffect(() => {
    totalBeatsRef.current = totalBeats;
  }, [totalBeats]);

  // Recreate the controller when the backing track changes. The loop
  // selection is in beats relative to this track, so it resets too.
  useEffect(() => {
    preloadAudioFile(src);
    const ctrl = new EditorAudioController({ src, bpm, offsetMs });
    controllerRef.current = ctrl;
    setIsPlaying(false);
    setIsLooping(false);
    setLoopRegionState(null);
    loopRegionRef.current = null;
    return () => {
      controllerRef.current = null;
      void ctrl.destroy();
    };
  }, [src, bpm, offsetMs]);

  // One rAF pump drives the playhead for both modes while playing.
  useEffect(() => {
    if (!isPlaying) {
      return;
    }
    let raf = 0;
    const tick = () => {
      const ctrl = controllerRef.current;
      if (!ctrl) {
        setIsPlaying(false);
        setIsLooping(false);
        return;
      }
      const beat = ctrl.getCurrentBeat();
      const looping = ctrl.isLooping();
      // Forward playback settles at the chart end even when the audio file
      // runs longer than the chart; looping wraps inside its window.
      if (!looping && (!ctrl.isPlaying() || beat >= totalBeatsRef.current)) {
        ctrl.pause();
        setPlayheadBeat(Math.min(beat, totalBeatsRef.current));
        setIsPlaying(false);
        setIsLooping(false);
        return;
      }
      setPlayheadBeat(beat);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isPlaying]);

  const playFrom = useCallback((beat: number) => {
    const ctrl = controllerRef.current;
    if (!ctrl) {
      return;
    }
    setError(null);
    ctrl
      .playFrom(beat)
      .then((started) => {
        // The start beat can be past the end of the audio (chart longer than
        // the file); only flip to "playing" if a source actually started.
        if (!started) {
          return;
        }
        setIsLooping(false);
        setIsPlaying(true);
      })
      .catch((err: unknown) => {
        console.error('Editor preview failed to play:', err);
        setError('Audio preview failed to start.');
      });
  }, []);

  const pause = useCallback(() => {
    const ctrl = controllerRef.current;
    if (!ctrl) {
      return;
    }
    const settled = ctrl.pause();
    setPlayheadBeat(settled);
    setIsPlaying(false);
    setIsLooping(false);
  }, []);

  const toggleForward = useCallback(
    (beat: number) => {
      const ctrl = controllerRef.current;
      if (!ctrl) {
        return;
      }
      // Pause only when already playing *forward*. A running loop switches into
      // forward play instead of stopping, so "Play" always plays forward.
      if (ctrl.isPlaying() && !ctrl.isLooping()) {
        pause();
        return;
      }
      playFrom(beat);
    },
    [pause, playFrom],
  );

  const startLoop = useCallback((region?: LoopRegionBeats) => {
    const ctrl = controllerRef.current;
    if (!ctrl) {
      return;
    }
    const target = region ?? loopRegionRef.current;
    if (!target) {
      return;
    }
    setError(null);
    // Show the requested window immediately for snappy feedback; reconcile to
    // the clamped window the controller actually loops once it resolves.
    loopRegionRef.current = target;
    setLoopRegionState(target);
    ctrl
      .playLoop(target)
      .then((clamped) => {
        if (!clamped) {
          return;
        }
        loopRegionRef.current = clamped;
        setLoopRegionState(clamped);
        setIsLooping(true);
        setIsPlaying(true);
      })
      .catch((err: unknown) => {
        console.error('Editor loop failed to play:', err);
        setError('Audio preview failed to start.');
      });
  }, []);

  const toggleLoop = useCallback(
    (fallback?: LoopRegionBeats) => {
      const ctrl = controllerRef.current;
      if (!ctrl) {
        return;
      }
      if (ctrl.isLooping()) {
        pause();
        return;
      }
      startLoop(loopRegionRef.current ?? fallback);
    },
    [pause, startLoop],
  );

  const clearLoop = useCallback(() => {
    loopRegionRef.current = null;
    setLoopRegionState(null);
    if (controllerRef.current?.isLooping()) {
      pause();
    }
  }, [pause]);

  return useMemo(
    () => ({
      isPlaying,
      isLooping,
      loopRegion,
      playheadBeat,
      error,
      playFrom,
      toggleForward,
      pause,
      startLoop,
      toggleLoop,
      clearLoop,
    }),
    [
      isPlaying,
      isLooping,
      loopRegion,
      playheadBeat,
      error,
      playFrom,
      toggleForward,
      pause,
      startLoop,
      toggleLoop,
      clearLoop,
    ],
  );
}
